import { useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useJobMessages } from "@/hooks/useJobMessages";
import { JobChatMessageRenderer } from "./JobChatMessageRenderer";
import { TiptapComposer } from "./TiptapComposer";
import { JobMessageService } from "@/services/job-message.service";
import { queryKeys } from "@/lib/queryKeys";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { createJobMessage, getJobParticipantIds, normalizeSenderRole } from "@/data/jobMessageTypes";
import type { MessageCategory } from "@/data/jobMessageTypes";
import type { Job } from "@/data/types";
import { toast } from "sonner";

interface JobChatThreadProps {
  jobId: string;
  category: MessageCategory;
  currentUserId: string;
  jobNumber: string;
  job: Pick<Job, "id" | "jobNumber" | "salesRep" | "assignedCrew" | "assignedSubcontractorId" | "assignedSubcontractor">;
}

export function JobChatThread({
  jobId,
  category,
  currentUserId,
  jobNumber,
  job,
}: JobChatThreadProps) {
  const { profile } = useCurrentUser();
  const { messages, isLoading, error } = useJobMessages(jobId, category);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (content: {
    html: string;
    text: string;
    mentionUserIds: string[];
  }) => {
    const senderName = profile?.full_name ?? profile?.name ?? "Unknown";
    const senderRole = normalizeSenderRole(profile?.role);

    const senderId = profile?.id ?? currentUserId;
    const participantIds = job
      ? getJobParticipantIds(job, senderId).concat(senderId)
      : [senderId];
    const recipientIds = [...new Set(participantIds)].filter(Boolean);

    const tempId = `temp-${Date.now()}`;
    const tempMessage = createJobMessage(
      jobId,
      content.html,
      { id: senderId, name: senderName, role: senderRole },
      recipientIds,
      { category, mentionUserIds: content.mentionUserIds },
    );
    tempMessage.id = tempId;

    const qc = queryClient;
    qc.setQueryData(
      queryKeys.jobMessages(jobId, category),
      (old: typeof messages | undefined) => {
        const arr = Array.isArray(old) ? [...old] : [];
        arr.push(tempMessage);
        return arr;
      },
    );

    try {
      const { data, error } = await JobMessageService.create(tempMessage, jobNumber);
      if (error || !data) {
        throw error ?? new Error("Failed to send message");
      }

      // Mentions are processed atomically server-side inside job-message-send;
      // no separate client-side invocation needed here.
      qc.setQueryData(
        queryKeys.jobMessages(jobId, category),
        (old: typeof messages | undefined) => {
          const arr = Array.isArray(old) ? [...old] : [];
          const idx = arr.findIndex((m) => m.id === tempId);
          if (idx >= 0) {
            arr[idx] = data;
          } else {
            arr.push(data);
          }
          return arr.sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          );
        },
      );

      return data;
    } catch (err) {
      console.error("[JobChatThread] send failed:", err);
      qc.setQueryData(
        queryKeys.jobMessages(jobId, category),
        (old: typeof messages | undefined) => {
          const arr = Array.isArray(old) ? [...old] : [];
          return arr.filter((m) => m.id !== tempId);
        },
      );
      toast.error("Message failed to send");
      throw err;
    }
  };

  if (error) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        Failed to load messages.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3 p-4 pb-2 min-h-[200px]"
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No messages in this tab yet.
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOwn = msg.senderId === (profile?.id ?? currentUserId);
            const showAvatar = !isOwn && (
              idx === 0 ||
              messages[idx - 1]?.senderId !== msg.senderId
            );
            const showTail = true;

            return (
              <JobChatMessageRenderer
                key={msg.id}
                message={msg}
                isOwnMessage={isOwn}
                showAvatar={showAvatar}
                showTail={showTail}
              />
            );
          })
        )}
      </div>

      <div className="border-t border-border/40 p-3 bg-background">
        <TiptapComposer
          jobId={jobId}
          onSend={handleSend}
          isOrdersTab={category === "orders"}
        />
      </div>
    </div>
  );
}
